import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { UserFilters as UserFiltersType } from '../../types';

interface UserFiltersProps {
  onFilterChange: (filters: UserFiltersType) => void;
  activeFilter: string;
  onActiveFilterChange: (key: string) => void;
  resultsCount: number;
}

export const UserFilters: React.FC<UserFiltersProps> = ({
  onFilterChange,
  activeFilter,
  onActiveFilterChange,
  resultsCount
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  const handleFilterClick = (filterId: string) => {
    onActiveFilterChange(filterId);
    
    if (filterId.startsWith('role-')) {
      onFilterChange({
        role: filterId.replace('role-', '') as any,
        status: 'all',
        search: searchQuery
      });
    } else if (filterId.startsWith('status-')) {
      onFilterChange({
        role: 'all',
        status: filterId.replace('status-', '') as any,
        search: searchQuery
      });
    }
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    onFilterChange({
      role: activeFilter.startsWith('role-') ? 
        activeFilter.replace('role-', '') as any : 'all',
      status: activeFilter.startsWith('status-') ? 
        activeFilter.replace('status-', '') as any : 'all',
      search: value
    });
  };

  const filterOptions = [
    { key: 'role-all', label: t('admin.userManagement.filters.allRoles', 'Tous les rôles') },
    { key: 'role-admin', label: t('admin.userManagement.roles.admin', 'Administrateur') },
    { key: 'role-bureau', label: t('admin.userManagement.roles.bureau', 'Bureau') },
    { key: 'role-expert', label: t('admin.userManagement.roles.expert', 'Expert') },
    { key: 'role-client', label: t('admin.userManagement.roles.client', 'Client') },
    { key: 'role-banque', label: t('admin.userManagement.roles.banque', 'Banque') },
    { key: 'status-all', label: t('admin.userManagement.filters.allStatus', 'Tous les statuts') },
    { key: 'status-active', label: t('admin.userManagement.filters.active', 'Actif') },
    { key: 'status-frozen', label: t('admin.userManagement.filters.frozen', 'Gelé') }
  ];

  return (
    <div className="filters-container">
      <div className="filters__search">
        <div className="filters__search-wrapper">
          <MagnifyingGlassIcon className="filters__search-icon" />
          <input
            type="text"
            className="filters__search-input"
            placeholder={t('admin.userManagement.filters.searchPlaceholder', 'Rechercher par nom ou email...')}
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="filters__clear-btn"
              onClick={() => handleSearch('')}
              aria-label="Clear search"
            >
              <XMarkIcon className="filters__clear-icon" />
            </button>
          )}
        </div>
      </div>

      <div className="filters__buttons">
        {filterOptions.map(option => (
          <button
            key={option.key}
            className={`filter-button ${
              activeFilter === option.key ? 'filter-button--active' : ''
            }`}
            onClick={() => handleFilterClick(option.key)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="filters__results">
        {resultsCount} {t('admin.userManagement.messages.usersFound', 'utilisateur(s) trouvé(s)')}
      </div>
    </div>
  );
};